import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DeliverableCategory, DeliverableStatus, Deliverable } from '@/types/deliverables';
import { Lead } from '@/types/lead';
import { User } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface AddDeliverableModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliverable?: Deliverable | null;
  preselectedLeadId?: string;
}

export const AddDeliverableModal: React.FC<AddDeliverableModalProps> = ({
  isOpen,
  onClose,
  deliverable,
  preselectedLeadId
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEditing = !!deliverable;

  const [formData, setFormData] = useState({
    leadId: preselectedLeadId || deliverable?.leadId || '',
    title: deliverable?.title || '',
    description: deliverable?.description || '',
    category: (deliverable?.category || 'SERVICE') as DeliverableCategory,
    committedDate: deliverable?.committedDate?.split('T')[0] || '',
    assignedToId: deliverable?.assignedToId || '',
    status: (deliverable?.status || 'PENDING') as DeliverableStatus,
    deliveredDate: deliverable?.deliveredDate?.split('T')[0] || '',
    proofOfDelivery: deliverable?.proofOfDelivery || '',
    customerFeedback: deliverable?.customerFeedback || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch WON leads
  const { data: wonLeads = [] } = useQuery<Lead[]>({
    queryKey: ['leads', { stage: 'WON' }],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/leads?stage=WON`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch leads');
      const result = await response.json();
      // Handle wrapped response {success, data: [...]}
      return result.data || result.leads || result;
    },
    enabled: isOpen
  });

  // Fetch all users
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const result = await response.json();
      // Handle wrapped response {success, data: [...]}
      return result.data || result.users || result;
    },
    enabled: isOpen
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create deliverable');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast({
        title: 'Success',
        description: 'Deliverable created successfully'
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/deliverables/${deliverable?.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update deliverable');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast({
        title: 'Success',
        description: 'Deliverable updated successfully'
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.leadId) newErrors.leadId = 'Lead is required';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (formData.title.length > 255) newErrors.title = 'Title must be less than 255 characters';
    if (!formData.committedDate) newErrors.committedDate = 'Committed date is required';

    if (['DELIVERED', 'VERIFIED'].includes(formData.status) && !formData.deliveredDate) {
      newErrors.deliveredDate = 'Delivered date is required for this status';
    }

    if (formData.deliveredDate && formData.committedDate) {
      const committed = new Date(formData.committedDate);
      const delivered = new Date(formData.deliveredDate);
      if (delivered < committed) {
        newErrors.deliveredDate = 'Delivered date must be on or after committed date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = {
      ...formData,
      description: formData.description || undefined,
      assignedToId: formData.assignedToId || undefined,
      deliveredDate: formData.deliveredDate || undefined,
      proofOfDelivery: formData.proofOfDelivery || undefined,
      customerFeedback: formData.customerFeedback || undefined
    };

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Deliverable' : 'Add New Deliverable'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Lead Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lead <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.leadId}
              onChange={(e) => setFormData({ ...formData, leadId: e.target.value })}
              className={cn(
                'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                errors.leadId ? 'border-red-500' : 'border-gray-300'
              )}
              disabled={isEditing}
            >
              <option value="">Select a WON lead...</option>
              {wonLeads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.name} - {lead.company}
                </option>
              ))}
            </select>
            {errors.leadId && (
              <p className="mt-1 text-sm text-red-500">{errors.leadId}</p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={cn(
                'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                errors.title ? 'border-red-500' : 'border-gray-300'
              )}
              placeholder="e.g., CRM Software License"
              maxLength={255}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Additional details about the deliverable..."
            />
          </div>

          {/* Category and Status Row */}
          <div className={cn('grid gap-4', isEditing ? 'grid-cols-2' : 'grid-cols-1')}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as DeliverableCategory })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="PROPOSAL">Proposal</option>
                <option value="CONTRACT">Contract</option>
                <option value="IMPLEMENTATION">Implementation</option>
                <option value="INSTALLATION">Installation</option>
                <option value="TRAINING">Training</option>
                <option value="SUPPORT">Support</option>
                <option value="DOCUMENTATION">Documentation</option>
                <option value="LICENSE">License</option>
                <option value="SERVICE">Service</option>
                <option value="EQUIPMENT">Equipment</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as DeliverableStatus })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="PENDING">Pending</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DELAYED">Delayed</option>
                </select>
              </div>
            )}
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Committed Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.committedDate}
                onChange={(e) => setFormData({ ...formData, committedDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  errors.committedDate ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.committedDate && (
                <p className="mt-1 text-sm text-red-500">{errors.committedDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivered Date
                {['DELIVERED', 'VERIFIED'].includes(formData.status) && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <input
                type="date"
                value={formData.deliveredDate}
                onChange={(e) => setFormData({ ...formData, deliveredDate: e.target.value })}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  errors.deliveredDate ? 'border-red-500' : 'border-gray-300'
                )}
                disabled={!['DELIVERED', 'VERIFIED'].includes(formData.status)}
              />
              {errors.deliveredDate && (
                <p className="mt-1 text-sm text-red-500">{errors.deliveredDate}</p>
              )}
            </div>
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned To
            </label>
            <select
              value={formData.assignedToId}
              onChange={(e) => setFormData({ ...formData, assignedToId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </option>
              ))}
            </select>
          </div>

          {/* Proof of Delivery */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proof of Delivery
            </label>
            <textarea
              value={formData.proofOfDelivery}
              onChange={(e) => setFormData({ ...formData, proofOfDelivery: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
              placeholder="Link to tracking, screenshot URL, or delivery confirmation..."
            />
          </div>

          {/* Customer Feedback */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Feedback
            </label>
            <textarea
              value={formData.customerFeedback}
              onChange={(e) => setFormData({ ...formData, customerFeedback: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Customer comments or feedback..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : isEditing
                ? 'Update Deliverable'
                : 'Create Deliverable'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
